/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, EventEmitter, Injector, Input, Output, ViewChild } from '@angular/core';
import {
    type ColumnDef, type Command, type HttpResponse, type TableConfig, FormControl,
    FormGroup, FormSlideWindowAbstract, TransferTableComponent, UNPLACEHOLDER,
    UtilService,
} from '@dm/core';
import { SelectOption } from '@dm/core/types';
import { LicenseKey } from 'app/common/constants/license-key';
import { SmartGuiService, TaskService } from 'app/common/services';
import { type DataTurboClient, type DataTurboShare, type FileSystem, type WindowsLocalUserOrGroup } from 'app/common/types/mo';
import { FileSystemEntryE } from 'app/feature/service/file-service/file-system/create-file-system/create-file-system.component';
import { isArray } from 'lodash';
import { type Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { type SmartguiDataTurboshareReceived } from '../../share';
import { DataTurboShareService } from '../data-turbo-share.service';
import { type ErrorMessage, DataTurboShareStrService, PermissionTypeE, UserTypeE } from '../data-turbo-share-str.service';

@Component({
    selector: 'app-add-data-turbo-user',
    templateUrl: './add-data-turbo-user.component.html',
    styleUrls: ['./add-data-turbo-user.component.scss'],
})
export class AddDataTurboUserComponent extends FormSlideWindowAbstract implements OnInit {
    @Input() isFrontTable = false;
    @Input() isFromDetail = false;
    @Input() vstoreId: string;
    @Input() selectedVstore: SelectOption;
    // 外部已选择的用户/用户组
    @Input() DataTurboUsers: DataTurboClient[] = [];
    @Input() entry: FileSystemEntryE;
    @Input() dataTurboShare: DataTurboShare;

    // 默认支持，create as和创审计日志文件系统的copyfrom下不支持c
    @Input() supportSmartGui = true;
    @Output() callBack: EventEmitter<DataTurboClient[]> = new EventEmitter();
    @ViewChild('transferTable') transferTable: TransferTableComponent;

    closeArgs: {
        refresh: boolean;
        data: DataTurboClient[];
    } = {
            refresh: false,
            data: null,
        };
    i18n = this.str.i18n;
    winTitle: string;
    loading = false;

    smartGuiLoading = false;

    userTypeOpts = this.str.userTypeOpts;
    UserTypeE = UserTypeE;
    FileSystemEntryE = FileSystemEntryE;
    LicenseKey = LicenseKey;

    get userType(): UserTypeE {
        return this.vm ? this.vm.value.userType : UserTypeE.DataTurbo;
    }

    userData: DataTurboClient[];
    windowsSelection: Array<WindowsLocalUserOrGroup & DataTurboClient> = [];
    tableConfig: TableConfig;
    tableColumns: ColumnDef[];

    vm: FormGroup;
    errorMessage: ErrorMessage;

    showErrorMsg = false;
    showGuideTip = false;

    UNPLACEHOLDER = UNPLACEHOLDER;

    /**
     * 权限级别选项，默认是完整的
     */
    permissionLevelOpts: SelectOption[] = this.str.permissionLevelOpts;

    constructor(
        injector: Injector,
        public str: DataTurboShareStrService,
        private service: DataTurboShareService,
        public utilService: UtilService,
        private smartguiService: SmartGuiService,
        private taskService: TaskService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.initTable();

        this.smartGuiLoading = this.supportSmartGui && this.smartguiService.switch;

        super.ngOnInit();

        if (!this.isFrontTable) {
            this.vstoreId = this.dataTurboShare.vstoreId;
        }

        this.getUser();

        this.windowVisible = true;
        this.pageReady = true;

        // 直接从共享添加用户/用户组，entry为undefined, 需要先查是不是属于审计日志文件系统
        if (this.dataTurboShare && this.dataTurboShare?.fsId !== UNPLACEHOLDER && !this.entry) {
            this.loading = true;
            this.service.getFileSystem(this.dataTurboShare.fsId, this.dataTurboShare.vstoreId)
                .subscribe((data: FileSystem) => {
                    this.loading = false;
                    if (!data) {
                        return;
                    }
                    // 暂不支持智能推荐
                }, () => {
                    this.loading = false;
                });
        } else {
            // 暂不支持智能推荐
        }
    }

    initVm(): void {
        this.vm = new FormGroup({
            userType: new FormControl(this.userType),
            permission: new FormControl(PermissionTypeE.ReadAndWrite, []),
        }, { updateOn: 'blur' });
    }

    initTable(): void {
        this.tableConfig = {
            dataKey: 'NAME',
            selectionMode: 'checkbox',
            checkRowDisabled: (data) => ({ disabled: this.selectedDisabled(data) }),
        };

        this.tableColumns = [
            {
                header: this.i18n.label.name,
                field: 'NAME',
                filterType: 'search',
                filterTrim: false,
            },
        ];
    }

    isModified(): boolean {
        return true;
    }

    isDataTurbo(userType: UserTypeE): boolean {
        return userType === UserTypeE.DataTurbo;
    }

    getUser(): void {
        this.service.getAvaliableUserBatchRest(this.vstoreId)
            .pipe(finalize(() => this.loading = false))
            .subscribe(({ data }: { data: DataTurboClient[] }) => {
                const userArr = isArray(data) ? data : [data];
                const userData = data ? userArr : [];
                // 增加解码的用户名
                userData.forEach((item) => item.NAME_DECODE = this.utilService.decodeHTML(item.NAME));

                // 过滤已存在的权限
                this.userData = userData.filter((user: DataTurboClient) => {
                    const isExist = this.DataTurboUsers.some((value) => value.userName === user.NAME);
                    return !isExist;
                });
            });
    }

    onSelectionChange(): void {
        // 选项变化时隐藏错误提示
        this.showErrorMsg = !this.windowsSelection.length;
    }

    /**
     * 是否disable推拉表格里已经选择过的windows用户/用户组
     */
    selectedDisabled(data: WindowsLocalUserOrGroup): boolean {
        for (const item of this.DataTurboUsers) {
            if (this.vm.value.userType === UserTypeE.DataTurbo && item.userName === data.NAME) {
                // 用户
                return true;
            }
        }
        return false;
    }

    collectionClients(): DataTurboClient[] {
        let data: DataTurboClient[] = [];
        const isDataTurbo = this.vm.value.userType === UserTypeE.DataTurbo;
        data = isDataTurbo ?
            this.windowsSelection.map((item) => (
                this.isFromDetail ? {
                    userName: item.NAME,
                    permission: String(PermissionTypeE.ReadAndWrite),
                    vstoreId: this.dataTurboShare.vstoreId,
                    shareId: this.dataTurboShare.ID,
                } : {
                    userName: item.NAME,
                    PERMISSION: String(PermissionTypeE.ReadAndWrite),
                    ID: item.ID,
                })) :
            [{
                ID: `Everyone-${UserTypeE.Everyone}`,
                NAME: '@Everyone',
            }];

        return data;
    }

    buildCmds(): Command[] {
        const cmds: Command[] = [];

        const datas = this.collectionClients().filter((item) => !this.DataTurboUsers.some((value) => value.userName === item.userName));
        if (this.isFromDetail) {
            return this.buildDadaTurboClientCmds(datas);
        } else {
            this.callBack.emit(datas);
        }
        return cmds;
    }

    /**
     * 构建添加客户端的rest
     */
    buildDadaTurboClientCmds(datas: DataTurboClient[]): Command[] {
        const cmds: Command[] = [];
        const params: DataTurboShare = {};
        params.auth = datas;

        cmds.push({
            name: this.i18n.title.addDataTurboPermission(this.dataTurboShare.sharePath, String(PermissionTypeE.ReadAndWrite)),
            action: this.service.addClients(this.dataTurboShare.sharePath, params),
        });

        return cmds;
    }

    /**
     * 获取DadaTurbo智能推荐数据
     */
    getDadaTurboSmartguiData(): void {
        if (!this.smartguiService.switch || !this.supportSmartGui) {
            this.loading = false;
            return;
        }

        this.showLoading();
        this.service.getSmartguiDataTurbo(this.vstoreId)
            .pipe(finalize(() => {
                this.hideLoading();
                this.smartGuiLoading = false;
            }))
            .subscribe((data: SmartguiDataTurboshareReceived) => {
                if (!data) {
                    return;
                }

                const dadaTurboClientTableHasEveryone =
                    this.DataTurboUsers.some((item) => item.NAME === '@Everyone');
                const userType = this.str.getUserType(data);
                const isRecommendEveryone = userType === UserTypeE.Everyone;
                // 外部表格数据包含Everyone且GUI推荐Everyone则设置默认用户类型，否则设置推荐类型
                if (isRecommendEveryone && dadaTurboClientTableHasEveryone) {
                    // 设置默认的用户类型
                    const userTypeItem = this.str.userTypeOpts.find((item) => item.value === UserTypeE.DataTurbo);
                    this.vm.get('userType').setValue(userTypeItem.value);
                } else {
                    // 设置推荐的用户类型
                    const userTypeItem = this.str.userTypeOpts.find((item) => item.value === userType);
                    this.vm.get('userType').setValue(userTypeItem.value);
                }
            }, () => {
                // 获取智能GUI数据失败，给用户类型一个值
                this.smartGuiLoading = false;
                this.vm.get('userType').setValue(this.userType);
            });
    }

    /**
     * 复写exec
     */
    exec(): void {
        const cmds = this.buildCmds();
        if (cmds && cmds.length === 1) {
            this.loading = true;
            (cmds[0].action as Observable<HttpResponse>)
                .pipe(finalize(() => {
                    this.loading = false;
                }))
                .subscribe(({ data }) => {
                    this.closeArgs.refresh = true;
                    this.hide();
                });
        } else {
            this.hide();
        }
    }

    /**
     * 复写ok函数，校验是否选择了权限
     */
    onOk(): void {
        // 一进来直接点确定不会触发该校验，所以需要手动触发一次
        this.showErrorMsg = !this.windowsSelection.length;
        if (this.showErrorMsg) {
            return;
        }
        super.onOk();
    }
}

